/**
 * Toss error code → the Korean sentence we actually show.
 *
 * The strings for the codes listed in COPY.md are VERBATIM from that document —
 * they are shipped copy, not developer prose, so they must not be paraphrased
 * here. Codes that COPY.md does not cover (ARCH.md §10 has ~40 of them) get a
 * message written in the same 해요체 voice, and are marked in the table below.
 *
 * `action` is the label COPY.md pairs with the message, plus where it goes, so
 * an empty/error state can offer the escape hatch instead of a dead end.
 */

export interface ErrorAction {
  label: string;
  to?: string;
}

export interface ApiErrorInfo {
  code: string;
  message: string;
  action?: ErrorAction;
}

const GENERIC = "요청을 처리하지 못했어요. 잠시 후 다시 시도해 주세요.";

const NETWORK = "서버에 연결할 수 없어요. 네트워크 상태를 확인하고 다시 시도해 주세요.";

/** COPY.md's 빈 상태 · 에러 문안 table, plus the rest of ARCH §10. */
const TABLE: Record<string, ApiErrorInfo> = {
  // ---- COPY.md, verbatim -------------------------------------------------
  "expired-token": {
    code: "expired-token",
    message:
      "토스증권 연결이 끊겼어요. 키가 만료됐을 수 있어요. 다시 연결하면 바로 이어서 사용할 수 있어요.",
    action: { label: "다시 연결하기", to: "/settings/api-key" },
  },
  "edge-blocked": {
    code: "edge-blocked",
    message:
      "토스증권이 이 요청을 차단했어요. 허용 IP 등록이 빠졌거나 잘못됐을 수 있어요. 트랑슈 고정 IP를 토스증권 앱 허용 IP 목록에 등록했는지 확인해 주세요.",
    action: { label: "IP 등록 방법 보기", to: "/onboarding/allow-ip" },
  },
  "insufficient-buying-power": {
    code: "insufficient-buying-power",
    message:
      "매수 가능 금액이 부족해서 주문이 실행되지 않았어요. 예수금을 확인하거나 주문 수량을 조정해 주세요.",
    action: { label: "계좌 확인하기", to: "/dashboard" },
  },
  "order-hours-closed": {
    code: "order-hours-closed",
    message:
      "지금은 장 운영시간이 아니에요. 다음 장이 열리면 조건을 다시 확인해서 주문을 실행할게요.",
    action: { label: "시장 운영시간 보기", to: "/settings/account" },
  },
  "stock-restricted": {
    code: "stock-restricted",
    message:
      "이 종목은 현재 거래가 제한되어 있어요. 정리매매, 투자경고 등 사유가 있을 수 있으니 종목 상태를 확인해 주세요.",
    action: { label: "종목 상세 보기" },
  },
  "rate-limit-exceeded": {
    code: "rate-limit-exceeded",
    message:
      "토스증권 API 요청이 잠시 몰렸어요. 정해진 시간이 지나면 자동으로 다시 시도할게요. 별도로 조치하지 않아도 괜찮아요.",
    action: { label: "상태 확인하기", to: "/dashboard" },
  },

  // ---- Not in COPY.md — written here in the same voice --------------------
  "invalid-token": {
    code: "invalid-token",
    message: "토스증권 인증에 실패했어요. API 키를 다시 등록하면 바로 이어서 사용할 수 있어요.",
    action: { label: "다시 연결하기", to: "/settings/api-key" },
  },
  "login-user-not-found": {
    code: "login-user-not-found",
    message: "토스증권에서 이 API 키를 찾을 수 없어요. 키가 폐기되지 않았는지 확인해 주세요.",
    action: { label: "다시 연결하기", to: "/settings/api-key" },
  },
  forbidden: {
    code: "forbidden",
    message: "이 요청에 필요한 권한이 없어요. 토스증권 앱에서 키 권한을 확인해 주세요.",
  },
  "invalid-request": {
    code: "invalid-request",
    message: "요청 내용에 문제가 있어요. 입력값을 다시 확인해 주세요.",
  },
  "unsupported-symbol": {
    code: "unsupported-symbol",
    message: "지원하지 않는 종목이에요. 종목 코드를 다시 확인해 주세요.",
  },
  "unsupported-ranking-duration": {
    code: "unsupported-ranking-duration",
    message: "이 랭킹에는 선택한 기간을 쓸 수 없어요. 다른 기간을 선택해 주세요.",
  },
  "insufficient-sellable-quantity": {
    code: "insufficient-sellable-quantity",
    message: "매도 가능 수량이 부족해요. 보유 수량을 확인해 주세요.",
  },
  "price-out-of-range": {
    code: "price-out-of-range",
    message: "주문 가격이 허용 범위를 벗어났어요. 상·하한가 안쪽 가격으로 다시 시도해 주세요.",
  },
  "invalid-tick-size": {
    code: "invalid-tick-size",
    message: "호가단위에 맞지 않는 가격이에요. 호가단위로 맞춘 뒤 다시 주문할게요.",
  },
  "opposite-pending-order-exists": {
    code: "opposite-pending-order-exists",
    message: "같은 종목에 반대 방향 미체결 주문이 있어요. 그 주문이 정리된 뒤에 다시 시도할게요.",
    action: { label: "주문 내역 보기", to: "/orders" },
  },
  "order-type-not-allowed": {
    code: "order-type-not-allowed",
    message: "이 종목에는 선택한 주문 유형을 쓸 수 없어요.",
  },
  "order-limit-exceeded": {
    code: "order-limit-exceeded",
    message: "토스증권 주문 한도를 넘었어요. 오늘 주문 내역을 확인해 주세요.",
    action: { label: "주문 내역 보기", to: "/orders" },
  },
  "confirm-high-value-required": {
    code: "confirm-high-value-required",
    message:
      "1억원 이상 주문은 별도 확인이 필요해요. 설정에서 고액 주문을 허용하면 이어서 진행할 수 있어요.",
    action: { label: "한도 설정 열기", to: "/settings/limits" },
  },
  "max-order-amount-exceeded": {
    code: "max-order-amount-exceeded",
    message: "주문 금액이 토스증권 최대 한도를 넘었어요. 1회 진입금액을 줄여 주세요.",
    action: { label: "한도 설정 열기", to: "/settings/limits" },
  },
  "account-restricted": {
    code: "account-restricted",
    message: "이 계좌로는 자동매매를 실행할 수 없어요. 일반 위탁계좌를 연결해 주세요.",
    action: { label: "계좌 설정 열기", to: "/settings/account" },
  },
  "prerequisite-required": {
    code: "prerequisite-required",
    message: "토스증권에서 먼저 처리해야 하는 절차가 남아 있어요. 토스증권 앱에서 확인해 주세요.",
  },
  "market-not-supported-for-stock": {
    code: "market-not-supported-for-stock",
    message: "이 종목은 선택한 거래소에서 거래할 수 없어요.",
  },
  "investor-exchange-not-integrated": {
    code: "investor-exchange-not-integrated",
    message:
      "토스증권 앱에서 거래소 통합 주문(SOR) 설정을 먼저 켜야 해요. 설정한 뒤 다시 시도해 주세요.",
  },
  "amount-order-outside-regular-hours": {
    code: "amount-order-outside-regular-hours",
    message: "금액 주문은 미국 정규장에서만 가능해요. 정규장이 열리면 다시 시도할게요.",
  },
  "fractional-quantity-outside-regular-hours": {
    code: "fractional-quantity-outside-regular-hours",
    message:
      "미국 주식 소수점 수량은 정규장 종료 1시간 전까지만 팔 수 있어요. 다음 정규장에서 다시 시도할게요.",
  },
  "us-modify-quantity-not-supported": {
    code: "us-modify-quantity-not-supported",
    message: "미국 주식 주문은 수량을 정정할 수 없어요. 취소하고 다시 주문해 주세요.",
  },
  "duplicate-conditional-order": {
    code: "duplicate-conditional-order",
    message:
      "이 종목에는 이미 조건주문이 걸려 있어요. 손절·익절을 나눠서 등록하고 서버에서 함께 지켜볼게요.",
  },
  "condition-already-met": {
    code: "condition-already-met",
    message: "등록하려는 조건이 이미 충족된 상태예요. 청산 여부를 다시 확인할게요.",
  },
  "idempotency-key-conflict": {
    code: "idempotency-key-conflict",
    message: "주문 처리 중 문제가 발견돼서 규칙을 즉시 멈췄어요. 확인 후 알려드릴게요.",
  },
  "request-in-progress": {
    code: "request-in-progress",
    message: "같은 요청이 처리되고 있어요. 잠시 뒤 다시 시도할게요.",
  },
  "already-filled": { code: "already-filled", message: "이미 체결된 주문이에요." },
  "already-canceled": { code: "already-canceled", message: "이미 취소된 주문이에요." },
  "already-modified": { code: "already-modified", message: "이미 정정된 주문이에요." },
  "already-rejected": { code: "already-rejected", message: "이미 거부된 주문이에요." },
  "already-processing": { code: "already-processing", message: "이미 처리 중인 주문이에요." },
  "modify-restricted": { code: "modify-restricted", message: "지금은 이 주문을 정정할 수 없어요." },
  "cancel-restricted": { code: "cancel-restricted", message: "지금은 이 주문을 취소할 수 없어요." },
  "stock-not-found": { code: "stock-not-found", message: "종목을 찾을 수 없어요." },
  "account-not-found": {
    code: "account-not-found",
    message: "계좌를 찾을 수 없어요. 계좌를 다시 연결해 주세요.",
    action: { label: "계좌 연결하기", to: "/onboarding/account" },
  },
  "order-not-found": { code: "order-not-found", message: "주문을 찾을 수 없어요." },
  "conditional-order-not-found": {
    code: "conditional-order-not-found",
    message: "조건주문을 찾을 수 없어요.",
  },
  "exchange-rate-not-found": {
    code: "exchange-rate-not-found",
    message: "환율 정보를 가져오지 못했어요. 잠시 후 다시 시도해 주세요.",
  },
  "edge-rate-limit-exceeded": {
    code: "edge-rate-limit-exceeded",
    message:
      "토스증권 API 요청이 잠시 몰렸어요. 정해진 시간이 지나면 자동으로 다시 시도할게요. 별도로 조치하지 않아도 괜찮아요.",
    action: { label: "상태 확인하기", to: "/dashboard" },
  },
  "internal-error": {
    code: "internal-error",
    message: "토스증권 쪽에서 오류가 났어요. 잠시 후 다시 시도할게요.",
  },
  maintenance: {
    code: "maintenance",
    message: "토스증권이 점검 중이에요. 점검이 끝나면 자동으로 다시 이어서 실행할게요.",
  },
  "unsupported-content-type": {
    code: "unsupported-content-type",
    message: "요청 형식이 올바르지 않아요.",
  },
  "account-header-required": {
    code: "account-header-required",
    message: "요청 처리 중 문제가 있었어요. 문제를 확인하고 있으니 잠시만 기다려 주세요.",
  },
  // ---- Tranche's own gates ----------------------------------------------
  // `safety-gate` 는 여기 없어요. 서버가 이 코드 하나로 게이트 10여 개를 내보내고
  // (`errors/app.py: SafetyGateError`) 각자 **한도 숫자가 든 다른 한국어 문장**을 실어
  // 보내거든요. 사본을 하나 적어 두면 그중 아홉은 거짓말이 됩니다 — 실제로 하루 매수
  // 한도(S2)에 걸린 저장이 "90일 이상 백테스트가 필요해요" 로 보였고, 운영자는 있지도
  // 않은 백테스트 조건을 찾아 헤맸어요. `SERVER_OWNS_MESSAGE` 를 보세요.
  "daily-order-limit-exceeded": {
    code: "daily-order-limit-exceeded",
    message: "오늘의 주문 한도를 넘어서 규칙을 멈췄어요. 한도를 확인하고 다시 시작해 주세요.",
    action: { label: "한도 설정 열기", to: "/settings/limits" },
  },
  "indicator-not-available": {
    code: "indicator-not-available",
    message: "토스증권 데이터로는 만들 수 없는 조건이에요. 다른 지표로 바꿔 주세요.",
  },
  "symbol-already-held": {
    code: "symbol-already-held",
    message:
      "같은 종목을 다른 규칙이 이미 보유하고 있어요. 손절·익절을 온전히 위임하려면 한 규칙만 보유해야 해요.",
  },
  // ---- book (rule book / ledger) -----------------------------------------
  // `book-cash-committed` 과 `book-cash-insufficient` 는 같은 422 라도 해법이 다르다
  // — 하나는 청산·취소가 먼저고, 하나는 돈을 채우거나 액수를 줄이는 것이다. 코드를
  // 하나로 합치면 화면이 둘 중 하나의 안내밖에 못 낸다.
  // `action` is intentionally omitted — this code only ever surfaces through
  // the fund/withdraw dialog's mutation, which the global interceptor shows
  // as a toast only (`toastStore` has no action/link slot). An `action` here
  // would be dead data, not an affordance.
  "book-cash-committed": {
    code: "book-cash-committed",
    message:
      "예약되거나 배치된 금액이 있어 인출할 수 없어요. 포지션을 청산하거나 미체결 주문을 취소한 뒤 다시 시도해 주세요.",
  },
  "book-cash-insufficient": {
    code: "book-cash-insufficient",
    message: "이 장부에 남은 금액이 부족해요. 배정 금액을 늘리거나 회수 금액을 줄여 주세요.",
  },
  // 서버 문구는 어느 통화인지에 따라 갈리지만(`routes/book.py: fund_book`) 이 표는
  // 한 문장만 담을 수 있어서 둘 다 적는다. 422 기본 문구("입력값을 확인해 주세요")로
  // 떨어지면 무엇을 어떻게 고쳐야 하는지가 사라진다.
  "book-currency-mismatch": {
    code: "book-currency-mismatch",
    message:
      "규칙 예산은 그 규칙이 거래하는 통화로만 배정·회수할 수 있어요. 미국 규칙은 달러, 국내 규칙은 원화예요.",
  },
  "book-over-allocated": {
    code: "book-over-allocated",
    message:
      "계좌 장부에 남은 여유보다 많이 배정할 수 없어요. 계좌 장부를 먼저 채우거나 다른 규칙의 배정을 줄여주세요.",
  },
  "book-quarantine-multiple-positions": {
    code: "book-quarantine-multiple-positions",
    message:
      "이 종목에 격리된 포지션이 더 있어요. 하나씩 풀면 계좌 실측 총량을 중복으로 배정하게 돼요. 종목 전체 해제로 한 번에 풀어 주세요.",
  },
  "book-holdings-snapshot-missing": {
    code: "book-holdings-snapshot-missing",
    message: "계좌 보유 스냅샷이 없어 격리를 해제할 수 없어요. 계좌를 먼저 동기화해 주세요.",
  },
};

/** 429 without a body still needs the rate-limit sentence. */
const BY_STATUS: Record<number, string> = {
  401: "로그인이 만료됐어요. 다시 로그인해 주세요.",
  403: TABLE["edge-blocked"].message,
  404: "요청한 정보를 찾을 수 없어요.",
  422: "입력값을 확인해 주세요.",
  429: TABLE["rate-limit-exceeded"].message,
  500: "서버에서 오류가 났어요. 잠시 후 다시 시도해 주세요.",
  502: NETWORK,
  503: "서버가 잠시 응답하지 못했어요. 잠시 후 다시 시도해 주세요.",
  504: "응답이 늦어지고 있어요. 잠시 후 다시 시도해 주세요.",
};

/**
 * 코드 하나가 **여러 사유**를 나르고, 서버 문장이 그 사유를 구별하는 유일한 자리인 코드들.
 *
 * 나머지 코드는 사본이 낫습니다 — 프록시를 통해 올라온 토스 오류는 영문이고 내부 용어를
 * 그대로 노출해요. 하지만 이 코드들은 우리 api 자신이 낸 것이라 이미 한국어 완성 문장이고,
 * 한도 숫자·계좌 사용량처럼 **사본이 가질 수 없는 값**이 문장 안에 들어 있어요.
 */
const SERVER_OWNS_MESSAGE: ReadonlySet<string> = new Set(["safety-gate"]);

/**
 * Resolve the message for a backend error.
 * The code table wins; then the HTTP status; then a generic sentence. The
 * server's own `message` is never surfaced raw — it is English and often quotes
 * Toss internals — **except** for the codes in `SERVER_OWNS_MESSAGE`.
 */
export function resolveApiError(
  code: string | null | undefined,
  status?: number | null,
  serverMessage?: string | null
): ApiErrorInfo {
  if (code && serverMessage && SERVER_OWNS_MESSAGE.has(code)) {
    return { ...TABLE[code], code, message: serverMessage };
  }
  if (code && TABLE[code]) return TABLE[code];
  if (status && BY_STATUS[status])
    return { code: code ?? String(status), message: BY_STATUS[status] };
  // 코드가 있는데 상태가 없으면 응답은 받은 것입니다 — 화면이 저장된 `last_error_code` 만
  // 넘기는 자리(주문 목록)라서, 여기서 네트워크 문구를 쓰면 토스의 거부가 연결 장애로 보입니다.
  if (!status && !code) return { code: "network", message: NETWORK };
  return { code: code ?? "unknown", message: GENERIC };
}

/** Every code we have shipped copy for — used by the tests and the admin view. */
export function knownErrorCodes(): string[] {
  return Object.keys(TABLE);
}
